import React, { useState, useEffect } from 'react'
import { withTranslation } from 'react-i18next'
import { useMutation, useQuery, gql } from '@apollo/client'
import { getRestaurantProfile, editRestaurant } from '../../../apollo'
import { Box, Typography, Input, Button, Alert } from '@mui/material'
import useStyles from '../styles'
import useGlobalStyles from '../../../utils/globalStyles'

const GET_RESTAURANT_PROFILE = gql`
  ${getRestaurantProfile}
`
const EDIT_RESTAURANT = gql`
  ${editRestaurant}
`

// The per-km delivery rate. Bag fee, minimum order and service fee are edited
// on the store profile, so this card only owns what the profile does not.
function StoreCharges(props) {
  const { t } = props
  const restaurantId =
    props.restaurantId || localStorage.getItem('restaurantId') || ''
  const userType = localStorage.getItem('user-borku')
    ? JSON.parse(localStorage.getItem('user-borku')).userType
    : null
  const isAdmin = userType === 'ADMIN'

  const [deliveryRate, setDeliveryRate] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const classes = useStyles()
  const globalClasses = useGlobalStyles()

  const { data, refetch } = useQuery(GET_RESTAURANT_PROFILE, {
    variables: { id: restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'network-only'
  })
  const [mutate, { loading }] = useMutation(EDIT_RESTAURANT)

  useEffect(() => {
    const restaurant = data?.restaurant
    if (!restaurant) return
    setDeliveryRate(String(restaurant.restDeliveryRate ?? ''))
  }, [data])

  useEffect(() => {
    if (!successMessage) return
    const timeoutId = setTimeout(() => setSuccessMessage(''), 3000)
    return () => clearTimeout(timeoutId)
  }, [successMessage])

  useEffect(() => {
    if (!errorMessage) return
    const timeoutId = setTimeout(() => setErrorMessage(''), 3000)
    return () => clearTimeout(timeoutId)
  }, [errorMessage])

  // Keeps a blank box from being saved as a negative number
  const onNumberChange = setter => event => {
    const value = event.target.value
    if (value === '') {
      setter('')
      return
    }
    setter(String(Math.max(0, Number(value))))
  }

  const onSave = event => {
    event.preventDefault()
    const restaurant = data?.restaurant
    if (!restaurant) return
    mutate({
      variables: {
        restaurantInput: {
          _id: restaurantId,
          // Required by the profile input even though they are untouched here
          name: restaurant.name,
          phone: restaurant.phone || '',
          ...(isAdmin ? { restDeliveryRate: Number(deliveryRate || 0) } : {})
        }
      },
      onCompleted: () => {
        refetch()
        setSuccessMessage(t('Configuration saved successfully!'))
      },
      onError: error => {
        setErrorMessage(t('An error occurred while saving configuration.'))
        console.error('Mutation error:', error)
      }
    })
  }

  return (
    <Box container className={classes.container}>
      <Box className={classes.flexRow}>
        <Box item className={classes.heading}>
          <Typography variant="h6" className={classes.text}>
            {t('StoreCharges')}
          </Typography>
        </Box>
      </Box>

      <Box className={classes.form}>
        <form>
          <Box>
            <Typography className={classes.labelText}>
              {t('DeliveryRatePerKm')}
            </Typography>
            <Input
              style={{ marginTop: -1 }}
              id="input-store-delivery-rate"
              name="input-store-delivery-rate"
              placeholder={t('DeliveryRatePerKm')}
              type="number"
              min={0}
              value={deliveryRate}
              onChange={onNumberChange(setDeliveryRate)}
              disabled={!isAdmin}
              disableUnderline
              className={globalClasses.input}
            />
          </Box>
          {isAdmin ? (
            <Box>
              <Button
                className={globalClasses.button}
                disabled={loading || !data?.restaurant}
                onClick={onSave}>
                {t('Save')}
              </Button>
            </Box>
          ) : null}
          <Box mt={2}>
            {successMessage && (
              <Alert
                className={globalClasses.alertSuccess}
                variant="filled"
                severity="success">
                {successMessage}
              </Alert>
            )}
            {errorMessage && (
              <Alert
                className={globalClasses.alertError}
                variant="filled"
                severity="error">
                {errorMessage}
              </Alert>
            )}
          </Box>
        </form>
      </Box>
    </Box>
  )
}

export default withTranslation()(StoreCharges)
